import React from "react";
import { styled } from "@mui/material/styles";
import * as Mui from "@mui/material";
import { renderToStaticMarkup } from "react-dom/server";
import { FaTint } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";

const convertSvg = (svg) => {
  const markup = renderToStaticMarkup(svg);
  const encoded = encodeURIComponent(markup);
  return `url('data:image/svg+xml;utf8,${encoded}')`;
};

const StyledSwitch = styled(Mui.Switch)(() => ({
  width: 62,
  height: 34,
  padding: 7,

  "& .MuiSwitch-switchBase": {
    margin: 1,
    padding: 0,
    transform: "translateX(6px)",

    "&.Mui-checked": {
      transform: "translateX(22px)",

      "& .MuiSwitch-thumb": {
        backgroundColor: "#e57373",
      },

      "& + .MuiSwitch-track": {
        backgroundColor: "#ffcdd2",
        opacity: 1,
        boxShadow: "0 0 8px rgba(229,115,115,0.6)",
      },
    },
  },

  "& .MuiSwitch-thumb": {
    width: 32,
    height: 32,

    "&::before": {
      content: "''",
      position: "absolute",
      width: "100%",
      height: "100%",
      left: 0,
      top: 0,
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center",
      backgroundImage: convertSvg(<FaTint color="#fff" />),
    },
  },

  "& .MuiSwitch-track": {
    opacity: 1,
    backgroundColor: "#bdbdbd",
    borderRadius: 10,
    boxShadow: "none",
  },
}));

export function BloodDonorSwitch({
  bloodDonorKey = "bloodDonor",
  bloodTypeKey = "bloodType",
  testIdPrefix
}) {
  const { setValue, watch, register } = useFormContext();
  const { t } = useTranslation();

  const bloodDonor = watch(bloodDonorKey);
  const bloodType = watch(bloodTypeKey);

  const handleSwitchChange = (event) => {
    const checked = event.target.checked;

    setValue(bloodDonorKey, checked, { shouldDirty: true });

    if (!checked) {
      setValue(bloodTypeKey, "", { shouldDirty: true });
    }
  };

  const handleBloodTypeChange = (event) => {
    setValue(bloodTypeKey, event.target.value, { shouldDirty: true });
  };

  return (
    <Mui.Stack
      spacing={1}
      direction={{ xs: "column", sm: "row" }}
    >
      <Mui.Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        border="1px solid #676767b9"
        borderRadius="5px"
        p={1}
        sx={{
          flex: "1 1 200px",
          minWidth: 200,
          pl: 1.7,
          width: { xs: "100%", sm: "auto" },
          maxHeight: { xs: 56, sm: "auto" }
        }}
      >
        <Mui.Typography color="text.secondary">
          {t("BloodDonor")}
        </Mui.Typography>

        <StyledSwitch
          checked={!!bloodDonor}
          onChange={handleSwitchChange}
          inputProps={
            testIdPrefix
              ? { "data-testid": `${testIdPrefix}.blood-donor-switch` }
              : undefined
          }
        />
      </Mui.Stack>

      <Mui.TextField
        {...register(bloodTypeKey)}
        value={bloodType || ""}
        onChange={handleBloodTypeChange}
        label={t("BloodType")}
        disabled={!bloodDonor}
        inputProps={{ maxLength: 30,...(testIdPrefix && {
            "data-testid": `${testIdPrefix}.blood-type-input`,
          }) }}
        sx={{
          color: "text.secondary",
          flex: "1 1 200px",
          minWidth: 200,
          width: { xs: "100%", sm: "auto" },

          "& .MuiInputBase-input.Mui-disabled": {
            WebkitTextFillColor: "rgba(255,255,255,0.7)",
          },

          "& .MuiInputLabel-root": {
            color: bloodDonor
              ? "primary"
              : "rgba(255,255,255,0.7)",
          },
        }}
      />
    </Mui.Stack>
  );
}